import Vue from "vue";
import AOS from "aos";
import "aos/dist/aos.css";
import BannerAi from "@/components/BannerAi.vue";
import InquiryButton from "@/components/InquiryButton.vue";

AOS.init();

interface FormField {
  label: string;
  value: any;
}

interface Form {
  username: FormField;
  apartment: FormField;
  dong: FormField;
  ho: FormField;
  phone: FormField;
  email: FormField;
  content: FormField;
}

export default Vue.extend({
  data() {
    return {
      tabs_labs: null,
      contact_tabs: 0, // 문의하기 폼 탭 모델
      check: ["white"],
      dialog: false,
      dialog_video: true,
      form: {
        username: { label: "", value: null },
        apartment: { label: "", value: null },
        dong: { label: "", value: null },
        ho: { label: "", value: null },
        phone: { label: "", value: null },
        email: { label: "", value: null },
        content: { label: "", value: null },
      } as Form,
    };
  },

  created() {
    this.updateFormLabels();
  },

  watch: {
    // 언어가 변경될 때마다 폼 라벨을 업데이트합니다.
    "$i18n.locale"() {
      this.updateFormLabels();
    },
    // 문의하기 탭이 변경될 때마다 폼 라벨과 내용을 초기화합니다.
    contact_tabs() {
      this.resetFormValues();
      this.updateFormLabels();
    },
  },

  methods: {
    // 현재 선택된 탭과 언어에 맞게 폼 라벨을 설정합니다.
    updateFormLabels() {
      let formLabels;
      switch (this.contact_tabs) {
        case 1: // 관리자 문의
          formLabels = this.$t("HomePage.contact.form.admin");
          break;
        case 2: // 제안서 신청
          formLabels = this.$t("HomePage.contact.form.admin");
          break;
        case 0: // 입주민 문의
        default:
          formLabels = this.$t("HomePage.contact.form.resident");
          break;
      }

      this.form.username.label = (formLabels as any).name;
      this.form.apartment.label =
        (formLabels as any).apartment || (formLabels as any).position;
      this.form.dong.label =
        (formLabels as any).dong || (formLabels as any).company;
      this.form.ho.label =
        (formLabels as any).ho || (formLabels as any).siteName;
      this.form.phone.label = (formLabels as any).phone;
      this.form.email.label = (formLabels as any).email;
      this.form.content.label = (formLabels as any).content;
    },
    HANDLE_DIALOG_OPEN() {
      this.$store.commit("common/setDialogAi", true);
    },
    HANDLE_SEND_SLACK() {
      if (!this.isPrivacyAgreed()) return;

      let requiredFields: (keyof Form)[];
      let inquiryType: string;

      switch (this.contact_tabs) {
        case 1: // 관리자
          requiredFields = ["username", "apartment", "dong", "ho", "phone"];
          inquiryType = this.$t("HomePage.contact.tabs.admins") as string;
          break;
        case 2: // 제안서
          requiredFields = ["username", "apartment", "dong", "ho", "phone"];
          inquiryType = this.$t("HomePage.contact.tabs.proposal") as string;
          break;
        case 0: // 입주민
        default:
          requiredFields = ["username", "apartment", "dong", "ho", "phone"];
          inquiryType = this.$t("HomePage.contact.tabs.residents") as string;
          break;
      }

      if (!this.areRequiredFieldsFilled(requiredFields, inquiryType)) return;

      const content = this.createContentMessage(inquiryType);
      this.sendSlackMessage(content);
    },

    isPrivacyAgreed(): boolean {
      if (this.check.length < 2) {
        this.$toasted.show(
          this.$t("ServicePages.AdminAdvantage.toasts.agreePrivacy") as string
        );
        return false;
      }
      return true;
    },

    areRequiredFieldsFilled(
      fields: (keyof Form)[],
      inquiryType: string
    ): boolean {
      for (const key of fields) {
        if (!this.form[key].value) {
          const fieldLabel = this.form[key].label;
          const message = `${this.$t(
            "ServicePages.AdminAdvantage.toasts.fillField",
            { field: fieldLabel }
          )}`;
          this.$toasted.show(message);
          return false;
        }
      }
      return true;
    },

    createContentMessage(type: string): string {
      const title = `📢 홈페이지-${this.$t(type)} `;
      const contentArr = [title];

      Object.keys(this.form).forEach((key) => {
        const typedKey = key as keyof Form;
        const value = this.form[typedKey].value;
        const label = this.form[typedKey].label;

        if (value) {
          contentArr.push(`${label}: ${value}`);
        }
      });

      return contentArr.join("\n");
    },

    resetFormValues() {
      Object.keys(this.form).forEach((key) => {
        const typedKey = key as keyof Form;
        this.form[typedKey].value = null;
      });
      this.check = ["white"];
    },

    sendSlackMessage(content: string) {
      this.$aws.lambda
        .invoke(
          process.env.VUE_APP_AWS_LAMBDA_CREATE_MESSAGE_SLACK as string,
          "RequestResponse",
          JSON.stringify({
            action: "OFFICIAL",
            content: content,
          })
        )
        .then(() => {
          this.resetFormValues();
          this.updateFormLabels();
          this.dialog = true;
        });
    },
  },
  computed: {
    usePrivacy() {
      return process.env.VUE_APP_PRIVACY;
    },
    dialogTitle(): string {
      return this.contact_tabs === 2
        ? (this.$t("HomePage.contactSection.dialog.titleProposal") as string)
        : (this.$t("HomePage.contactSection.dialog.titleInquiry") as string);
    },
    dialogText(): string {
      return this.contact_tabs === 2
        ? (this.$t("HomePage.contactSection.dialog.textProposal") as string)
        : (this.$t("HomePage.contactSection.dialog.textInquiry") as string);
    },
  },
  components: {
    BannerAi,
    AiModal: BannerAi,
    InquiryButton,
  },
  // 페이지별 메타태그 설정 (공통 메타태그는 자동으로 상속됨)
  metaInfo() {
    return {
      title: "홈 - " + (process.env.VUE_APP_NAME || ""),
      meta: [
        // 페이지별로 오버라이드할 메타태그들
        {
          name: "description",
          content:
            "채들과 함께하는 스마트 라이프 스타일. 입주민, 관리자, 시공사 모두를 위한 통합 플랫폼입니다.",
        },
        {
          property: "og:title",
          content: "홈 - " + (process.env.VUE_APP_NAME || ""),
        },
        {
          property: "og:description",
          content:
            "채들과 함께하는 스마트 라이프 스타일. 입주민, 관리자, 시공사 모두를 위한 통합 플랫폼입니다.",
        },
        {
          name: "twitter:title",
          content: "홈 - " + (process.env.VUE_APP_NAME || ""),
        },
        {
          name: "twitter:description",
          content:
            "채들과 함께하는 스마트 라이프 스타일. 입주민, 관리자, 시공사 모두를 위한 통합 플랫폼입니다.",
        },
      ],
    };
  },
});
